import type { CSSProperties } from 'react';
import type { ClientRoomState } from '@hitster/shared';
import { playerColor } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import { IconCrown } from './Icon.tsx';

/**
 * Der Endstand als Rangliste mit auffahrenden Balken.
 *
 * Bisher endete ein Abend mit einer Zeile Text — und auf dem Fernseher
 * blieb die Aufdeck-Karte des letzten Songs das grosse Bild. Das letzte
 * Bild des Abends war damit „irgendein Lied" statt „so ist es
 * ausgegangen".
 *
 * Die Spielerliste half nicht: sie steht in Beitrittsreihenfolge, nicht
 * nach Punkten. Wer gewonnen hat, musste man sich zusammensuchen.
 */
export function Podium({ state }: { state: ClientRoomState }) {
  const t = useT();

  /**
   * Absteigend nach Karten, bei Gleichstand nach Token.
   *
   * Kopie vor dem Sortieren: `state.players` kommt aus dem
   * Anwendungszustand, und `sort` arbeitet an Ort und Stelle. Ohne die
   * Kopie waere die Reihenfolge ueberall sonst mit verdreht.
   */
  const ranked = [...state.players].sort(
    (a, b) => b.timeline.length - a.timeline.length || b.tokens - a.tokens,
  );

  // Laengste Leiste als Bezug -- nicht das Kartenziel. Endet die Partie
  // vorzeitig, waeren sonst alle Balken kurz.
  const longest = Math.max(1, ...ranked.map((player) => player.timeline.length));

  return (
    <ol className="podium">
      {ranked.map((player, index) => {
        const isWinner = state.winnerIds.includes(player.id);

        return (
          <li
            key={player.id}
            className={`podium__row ${isWinner ? 'podium__row--winner' : ''}`}
            style={
              {
                '--player': playerColor(player.id),
                // Der Balken faehrt gestaffelt auf, von oben nach unten.
                '--delay': `${index * 90}ms`,
                '--share': `${(player.timeline.length / longest) * 100}%`,
              } as CSSProperties
            }
          >
            <span className="podium__place">{index + 1}</span>

            <span className="podium__body">
              <span className="podium__head">
                <span className="podium__name">
                  {player.name}
                  {isWinner && <IconCrown size="0.9em" />}
                </span>
                <span className="podium__score">
                  {t('game.cards', {
                    count: player.timeline.length,
                    target: state.settings.targetCards,
                  })}
                </span>
              </span>

              <span className="podium__track" aria-hidden="true">
                <span className="podium__bar" />
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
